<template>
  <nav :class="$style.breadcrumb" :aria-label="$awt('aw.common.components.breadcrumb.label')" tabindex="0">
    <template v-for="(breadcrumb, index) in breadcrumbs" :key="`link-${index}`">
      <component
        :is="hasTooltip ? 'aw-breadcrumb-category-tooltip' : 'span'"
        v-if="isActive(breadcrumb, index)"
        :category-id="breadcrumb.categoryId"
      >
        <lv-link
          :to="$localePath(breadcrumb.to)"
          :class="[$style.breadcrumbItem]"
        >
          {{ breadcrumb.token }}
        </lv-link>
      </component>
      <div v-else>
        <span
          :class="[$style.breadcrumbItem, $style.breadcrumbItemLastChild, {[$style.breadcrumbItemDisabled]: breadcrumb.disabled}]"
          :aria-current="index === breadcrumbs.length -1 ? 'page' : null"
        >
          {{ breadcrumb.token }}
        </span>
      </div>
      <span v-if="index !== breadcrumbs.length - 1" :key="`separator-${index}`" :class="[$style.breadcrumbSeparator]">
        ›
      </span>
    </template>
  </nav>
</template>

<script setup>
  import { computed } from 'vue';
  import { useNuxtApp } from 'nuxt/app';
  import { useJsonld } from '#jsonld';
  import { LvLink } from '~~/common/components/loginet-vue-shop/index';
  import { useBreadcrumbStore } from '~~/common/stores/breadcrumb';
  import AwBreadcrumbCategoryTooltip from './AwBreadcrumbCategoryTooltip.vue';

  defineOptions({
    name: 'AwBreadcrumbVersion2',
    components: {
      AwBreadcrumbCategoryTooltip,
    },
  });

  const props = defineProps({
    externalBreadcrumbs: {
      type: Array,
      default: null,
    },
    hasTooltip: {
      type: Boolean,
      default: false,
    },
  });

  const { $awRuntimeConfig, $localePath } = useNuxtApp();
  const breadcrumbStore = useBreadcrumbStore();

  const breadcrumbs = computed(() => {
    return props.externalBreadcrumbs || breadcrumbStore.breadcrumbs;
  });

  const isActive = (breadcrumb, index) => {
    return !breadcrumb.disabled && index !== breadcrumbs.value.length - 1;
  };

  useJsonld(() => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.value.map((breadcrumb, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: breadcrumb.token,
      item: $awRuntimeConfig.auchanApiBrowserHost + $localePath(breadcrumb.to),
    })),
  }));
</script>

<style module lang="scss" rel="stylesheet/scss">
$breadcrumb-color-primary: $color-text-primary;
$breadcrumb-color-disabled: $color-text-secondary;

.breadcrumb {
  display: flex;
  align-items: center;
  flex-direction: row;
  flex-wrap: wrap;
  width: 100%;
  min-height: 16px;
  gap: 8px;
  @include sm(max) {
    overflow: visible;
    white-space: normal;
  }
  @include desktop-small(min) {
    min-height: 20px;
  }

  &Item {
    font-family: $secondary-font;
    font-size: 12px;
    font-weight: $font-weight-medium-v2;
    font-style: normal;
    line-height: 16px;
    word-break: break-word;
    hyphens: auto;
    color: $breadcrumb-color-primary;

    @include desktop-small(min) {
      font-size: 14px;
      line-height: 20px;
    }

    &LastChild {
      font-weight: $font-weight-regular-v2;
      user-select: none;
      color: $breadcrumb-color-disabled;
      @include tablet(min) {
        font-weight: $font-weight-normal-v2;
      }

      &:hover {
        cursor: default;
      }
    }

    &Disabled {
      padding-top: 1px;
      user-select: none;
      color: $breadcrumb-color-disabled;

      &:hover {
        cursor: default;
        color: $breadcrumb-color-disabled;
      }
    }
  }

  &Separator {
    font-family: $secondary-font;
    font-size: 14px;
    font-weight: 500;
    font-style: normal;
    line-height: 20px;
    text-align: center;
    color: $color-text-tertiary;
  }
}
</style>
